import { HUB_EASE } from "../../activities/components/hub-top";

/**
 * Motion on the Stays pages. One curve, the site's own (the category rail and
 * the pinned bar move on it), and transform/opacity only: nothing here
 * animates a size, which is what stutters on an iPhone.
 */
export const STAYS_EASE = HUB_EASE;

/**
 * The site-wide UI zoom (`body { zoom: 0.9 }` from 768px up, see tailwind.css).
 * Window sizes (`innerHeight`, `100dvh`) are in UNZOOMED pixels while the page
 * is laid out in zoomed ones, so anything sized from the window divides by
 * this, and anything compared against on-screen rects multiplies by it.
 */
export function uiZoom(): number {
  if (typeof window === "undefined") return 1;
  const z = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--mj-ui-zoom"));
  return z > 0 ? z : 1;
}

/** True when the visitor asked their device for less motion. */
export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Cards on screen, in reading order, capped: nobody sees the 40th card move. */
export function visibleCards(root: HTMLElement | null, cap = 12): HTMLElement[] {
  if (!root) return [];
  const vh = window.innerHeight;
  return [...root.querySelectorAll<HTMLElement>("[id^='stay-hub-card-']")]
    .filter((el) => {
      const r = el.getBoundingClientRect();
      return r.bottom > 0 && r.top < vh;
    })
    .slice(0, cap);
}

/**
 * Results reshuffle: after a mood or filter change the cards on screen settle
 * in one after another, 40ms apart, instead of the grid swapping in one frame.
 * Reduced motion keeps a plain fade.
 */
export function staggerIn(cards: HTMLElement[]): void {
  const reduce = prefersReducedMotion();
  cards.forEach((el, i) => {
    el.animate(
      reduce
        ? [{ opacity: 0 }, { opacity: 1 }]
        : [
            { opacity: 0, transform: "translateY(10px) scale(0.98)" },
            { opacity: 1, transform: "none" },
          ],
      { duration: reduce ? 160 : 260, delay: reduce ? 0 : i * 40, easing: STAYS_EASE, fill: "backwards" },
    );
  });
}

/** Document-relative box of each card, keyed by id, for the list/map FLIP. */
export function measureCards(root: HTMLElement | null): Map<string, DOMRect> {
  const out = new Map<string, DOMRect>();
  if (!root) return out;
  for (const el of root.querySelectorAll<HTMLElement>("[id^='stay-hub-card-']")) {
    const r = el.getBoundingClientRect();
    out.set(el.id, new DOMRect(r.left + window.scrollX, r.top + window.scrollY, r.width, r.height));
  }
  return out;
}

/**
 * List to map: each card on screen glides from where it was to where the new
 * layout put it. Translate only, never scale: the card changes width between
 * four columns and two, and scaling would stretch its photo and text. The size
 * snaps; the position travels, which is what the eye follows.
 */
export function glideCards(root: HTMLElement | null, before: Map<string, DOMRect>): void {
  if (prefersReducedMotion() || before.size === 0) return;
  for (const el of visibleCards(root, 16)) {
    const was = before.get(el.id);
    if (!was) continue;
    const r = el.getBoundingClientRect();
    // Rects are on-screen pixels; a transform is in the page's zoomed ones.
    const z = uiZoom();
    const dx = (was.left - (r.left + window.scrollX)) / z;
    const dy = (was.top - (r.top + window.scrollY)) / z;
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;
    el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "none" }], {
      duration: 300,
      easing: STAYS_EASE,
    });
  }
}
